import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api.js';
import { useTheme } from './lib/theme.js';
import { SECTION, useDocumentTitle } from './lib/pageTitle.js';
// From its own module, not ui.js: ui.js pulls in @tremor/react, and this one
// import was enough to keep Tremor (and its charting dependency) in the entry
// chunk that /welcome downloads.
import { Avatar } from './components/Avatar.js';
import { ChunkBoundary } from './components/ChunkBoundary.js';
import { Login } from './pages/auth/Login.js';
import { ResetPassword } from './pages/auth/ResetPassword.js';
import { logout } from './authClient.js';
import type { UserMeResponse } from './lib/types.js';

// Every screen below this line is lazy. They were static imports, which put all
// 16 admin pages plus Tremor and its charting dependency in the entry chunk —
// fine when /portal and /admin were the only routes, wrong now that /welcome is
// served to paid traffic that has no account and will never open the console.
// The nav and shell stay in the entry chunk; only the page bodies split out.
// Spelled out one per line rather than through a helper: a generic wrapper
// widens the component type to ComponentType<any>, and every onSelect/onNav
// callback below then loses its parameter types.
const Overview       = React.lazy(() => import('./pages/admin/Overview.js').then(m => ({ default: m.Overview })));
const Users          = React.lazy(() => import('./pages/admin/Users.js').then(m => ({ default: m.Users })));
const UserDetail     = React.lazy(() => import('./pages/admin/UserDetail.js').then(m => ({ default: m.UserDetail })));
const Orgs           = React.lazy(() => import('./pages/admin/Orgs.js').then(m => ({ default: m.Orgs })));
const OrgDetail      = React.lazy(() => import('./pages/admin/OrgDetail.js').then(m => ({ default: m.OrgDetail })));
const Requests       = React.lazy(() => import('./pages/admin/Requests.js').then(m => ({ default: m.Requests })));
const Recordings     = React.lazy(() => import('./pages/admin/Recordings.js').then(m => ({ default: m.Recordings })));
const Patients       = React.lazy(() => import('./pages/admin/Patients.js').then(m => ({ default: m.Patients })));
const Costs          = React.lazy(() => import('./pages/admin/Costs.js').then(m => ({ default: m.Costs })));
const BugReports     = React.lazy(() => import('./pages/admin/BugReports.js').then(m => ({ default: m.BugReports })));
const Security       = React.lazy(() => import('./pages/admin/Security.js').then(m => ({ default: m.Security })));
const Events         = React.lazy(() => import('./pages/admin/Events.js').then(m => ({ default: m.Events })));
const Consents       = React.lazy(() => import('./pages/admin/Consents.js').then(m => ({ default: m.Consents })));
const AdminReferrals = React.lazy(() => import('./pages/admin/Referrals.js').then(m => ({ default: m.AdminReferrals })));
const AdminLinks = React.lazy(() => import('./pages/admin/Links.js').then(m => ({ default: m.AdminLinks })));
const AdminPaywalls  = React.lazy(() => import('./pages/admin/Paywalls.js').then(m => ({ default: m.AdminPaywalls })));
const Plans          = React.lazy(() => import('./pages/admin/Plans.js').then(m => ({ default: m.Plans })));
const AdminTemplates = React.lazy(() => import('./pages/admin/Templates.js').then(m => ({ default: m.AdminTemplates })));
const Controls       = React.lazy(() => import('./pages/admin/Controls.js').then(m => ({ default: m.Controls })));
const EmailTemplates = React.lazy(() => import('./pages/admin/EmailTemplates.js').then(m => ({ default: m.EmailTemplates })));
const AccountShell   = React.lazy(() => import('./pages/account/Shell.js').then(m => ({ default: m.AccountShell })));

const PageSpinner = () => (
  <div className="flex items-center justify-center py-24 text-sm text-gray-400">Loading…</div>
);

// ── Inline SVG icons ──────────────────────────────────────────────────────────

type IconProps = { className?: string };

const Ico: React.FC<{ d: string; className?: string }> = ({ d, className }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const HomeIcon       = ({ className }: IconProps) => <Ico d="M2.5 7.5 8 3l5.5 4.5V13a1 1 0 0 1-1 1h-2.5v-4h-4v4H3.5a1 1 0 0 1-1-1z" className={className} />;
const BoltIcon       = ({ className }: IconProps) => <Ico d="M9 2 4 9h3l-1 5 5-7H8z" className={className} />;
const ShieldIcon     = ({ className }: IconProps) => <Ico d="M8 2 3 4v4c0 3 2.2 5.6 5 6 2.8-.4 5-3 5-6V4z" className={className} />;
const CheckIcon      = ({ className }: IconProps) => <Ico d="m3.5 8 3 3 6-6" className={className} />;
const ChevronRightIcon = ({ className }: IconProps) => <Ico d="m6 4 4 4-4 4" className={className} />;
const LogoutIcon     = ({ className }: IconProps) => <Ico d="m4 4 8 8M12 4l-8 8" className={className} />;
const MoonIcon       = ({ className }: IconProps) => <Ico d="M13 9A5 5 0 0 1 7 3c-2.8 0-5 2.2-5 5s2.2 5 5 5a5 5 0 0 0 6-4z" className={className} />;

const UsersIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="6" r="2.5" /><path d="M2 13c0-2.2 1.8-4 4-4s4 1.8 4 4" /><circle cx="11.5" cy="5.5" r="2" /><path d="M10.5 9.2c2-.2 3.5 1.3 3.5 3.3" />
  </svg>
);
const BuildingIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.5" y="3" width="5" height="10" rx="0.5" /><rect x="8.5" y="6" width="5" height="7" rx="0.5" /><path d="M4 5.5h2M4 7.5h2M4 9.5h2M10 8h2M10 10h2" />
  </svg>
);
const ListIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2.5 4h11M2.5 8h11M2.5 12h11" />
  </svg>
);
const SearchIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="7" r="4" /><path d="m10 10 3 3" />
  </svg>
);
const SunIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="3" /><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.2 3.2l1.4 1.4M11.4 11.4l1.4 1.4M3.2 12.8l1.4-1.4M11.4 4.6l1.4-1.4" />
  </svg>
);

// ── Admin dashboard ───────────────────────────────────────────────────────────

type AdminPage = 'overview' | 'costs' | 'users' | 'orgs' | 'plans' | 'templates' | 'requests' | 'recordings' | 'patients' | 'bugreports' | 'security' | 'events' | 'consents' | 'referrals' | 'links' | 'paywalls' | 'controls' | 'emails';

const ReferralIcon = ({ className }: IconProps) => <Ico d="M4 8h8M8 4v8M3 3l10 10M13 3 3 13" className={className} />;
const LinkIcon     = ({ className }: IconProps) => <Ico d="M6.5 9.5l3-3M7 4.5l1-1a2.5 2.5 0 0 1 3.5 3.5l-1 1M9 11.5l-1 1A2.5 2.5 0 0 1 4.5 9l1-1" className={className} />;
const TagIcon      = ({ className }: IconProps) => <Ico d="M2 5a1 1 0 0 1 1-1h5l5 5-5 5H3a1 1 0 0 1-1-1V5zm3 3a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" className={className} />;
const BugIcon      = ({ className }: IconProps) => <Ico d="M6 6a2 2 0 0 1 4 0M4.5 6h7v4a3.5 3.5 0 0 1-7 0V6zM2 8h2.5M11.5 8H14M2.5 4l1.7 1.2M13.5 4l-1.7 1.2M2.5 12l1.7-1.2M13.5 12l-1.7-1.2" className={className} />;
const MicIcon      = ({ className }: IconProps) => <Ico d="M8 1.5a2 2 0 0 0-2 2v4a2 2 0 1 0 4 0v-4a2 2 0 0 0-2-2zM3.5 7.5a4.5 4.5 0 0 0 9 0M8 12v2.5" className={className} />;
const PawIcon = ({ className }: IconProps) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="4" cy="6" rx="1.5" ry="2" /><ellipse cx="7.3" cy="4.2" rx="1.5" ry="2" />
    <ellipse cx="10.9" cy="4.9" rx="1.5" ry="2" /><ellipse cx="13.2" cy="7.8" rx="1.4" ry="1.8" />
    <path d="M8.6 8.6c2 0 3.6 1.7 3.6 3.4 0 1.2-1 2-2.2 2-.9 0-1.2-.4-2.1-.4s-1.2.4-2.1.4c-1.2 0-2.2-.8-2.2-2 0-1.7 1.6-3.4 3.6-3.4z" />
  </svg>
);
const TemplateIcon = ({ className }: IconProps) => <Ico d="M2.5 2.5h11v11h-11v-11zM2.5 6h11M6 6v7.5" className={className} />;
const CostIcon     = ({ className }: IconProps) => <Ico d="M8 2v12M10.5 4.8A2.8 2.8 0 0 0 8 3.5C6.6 3.5 5.5 4.3 5.5 5.6S6.9 7.6 8 7.9s2.5.8 2.5 2.2S9.4 12.5 8 12.5a2.8 2.8 0 0 1-2.5-1.3" className={className} />;
const SlidersIcon  = ({ className }: IconProps) => <Ico d="M2.5 4.5h4M9.5 4.5h4M2.5 11.5h2M7.5 11.5h6M8 2.5v4M5.5 9.5v4" className={className} />;
const MailIcon     = ({ className }: IconProps) => <Ico d="M2 4.5h12v7H2zM2 5l6 4 6-4" className={className} />;

const ADMIN_NAV = [
  { section: 'Workspace', items: [
    { key: 'overview' as AdminPage, label: 'Overview',       Icon: HomeIcon },
    { key: 'costs'    as AdminPage, label: 'Cost',           Icon: CostIcon },
    { key: 'users'    as AdminPage, label: 'Users',          Icon: UsersIcon },
    { key: 'orgs'     as AdminPage, label: 'Organizations',  Icon: BuildingIcon },
    { key: 'plans'    as AdminPage, label: 'Plans',          Icon: TagIcon },
    { key: 'templates' as AdminPage, label: 'Templates',     Icon: TemplateIcon },
  ]},
  { section: 'Data', items: [
    { key: 'requests'   as AdminPage, label: 'AI Requests', Icon: BoltIcon },
    { key: 'recordings' as AdminPage, label: 'Recordings',  Icon: MicIcon },
    { key: 'patients'   as AdminPage, label: 'Patients',    Icon: PawIcon },
    { key: 'bugreports' as AdminPage, label: 'Bug Reports', Icon: BugIcon },
    { key: 'paywalls'   as AdminPage, label: 'Paywalls',    Icon: TagIcon },
    { key: 'security'  as AdminPage, label: 'Security',    Icon: ShieldIcon },
    { key: 'consents'  as AdminPage, label: 'Consents',    Icon: CheckIcon },
    { key: 'events'    as AdminPage, label: 'Logs',        Icon: ListIcon },
  ]},
  { section: 'Website', items: [
    { key: 'links'      as AdminPage, label: 'Links',       Icon: LinkIcon },
    { key: 'referrals'  as AdminPage, label: 'Referrals',   Icon: ReferralIcon },
  ]},
  { section: 'System', items: [
    { key: 'controls' as AdminPage, label: 'Controls', Icon: SlidersIcon },
    { key: 'emails'   as AdminPage, label: 'Emails',   Icon: MailIcon },
  ]},
];

// Derived from the nav so page order and hash routing can never drift from it.
const ADMIN_PAGES: AdminPage[] = ADMIN_NAV.flatMap(s => s.items.map(i => i.key));

// The hash is either `#<page>` or `#<page>/<id>`. The id form is what makes the
// patient ⇄ recording ⇄ generation pivots real links: an admin can paste one,
// reload on one, and the browser's Back button retraces the drill because each
// navigation pushes rather than replaces.
type AdminRoute = { page: AdminPage; id: string | null };

function adminRouteFromHash(): AdminRoute {
  const h = window.location.hash.replace(/^#\/?/, '');
  const slash = h.indexOf('/');
  const page = slash === -1 ? h : h.slice(0, slash);
  const id = slash === -1 ? '' : h.slice(slash + 1);
  if (!(ADMIN_PAGES as string[]).includes(page)) return { page: 'overview', id: null };
  return { page: page as AdminPage, id: id ? decodeURIComponent(id) : null };
}

function AdminApp({ me }: { me: UserMeResponse }) {
  const [route, setRoute] = useState<AdminRoute>(adminRouteFromHash);
  const { page, id: routeId } = route;
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const { theme, toggle } = useTheme();

  // Keep the URL in step with the route — id included, or landing on
  // `#patients/<id>` would immediately be rewritten back to `#patients`.
  useEffect(() => {
    const target = '#' + page + (routeId ? `/${routeId}` : '');
    if (window.location.hash !== target) window.history.replaceState(null, '', target);
  }, [page, routeId]);

  useEffect(() => {
    const onHashChange = () => setRoute(adminRouteFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Push (not replace) so browser Back returns you to wherever you pivoted from
  // — a patient reached from a recording goes back to that recording.
  const go = (p: AdminPage, id?: string | null) => {
    setRoute({ page: p, id: id ?? null });
    const target = '#' + p + (id ? `/${id}` : '');
    if (window.location.hash !== target) window.history.pushState(null, '', target);
  };

  const navigate = (p: AdminPage) => { setRoute({ page: p, id: null }); setSelectedUserId(null); setSelectedOrgId(null); setNavOpen(false); };
  const pageLabel = ADMIN_NAV.flatMap(s => s.items).find(i => i.key === page)?.label ?? 'Admin';

  // One string for the topbar crumb and the tab title, so a drill-down that
  // reads "Patient" in the breadcrumb can't read "Patients" in the tab.
  const crumb =
    selectedUserId && page === 'users' ? 'User Detail'
    : selectedOrgId && page === 'orgs' ? 'Organization'
    : routeId && page === 'patients' ? 'Patient'
    : routeId && page === 'recordings' ? 'Recording'
    : pageLabel;

  useDocumentTitle(crumb, SECTION.admin);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100 dark:bg-[#0B0F1A]">
      {/* Mobile backdrop */}
      {navOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden
        />
      )}

      {/* Sidebar */}
      <aside
        className={[
          'flex flex-col w-64 shrink-0 bg-white dark:bg-[#111827] border-r border-gray-200/80 dark:border-white/[0.06] shadow-sm',
          'fixed inset-y-0 left-0 z-50 transition-transform duration-200 lg:static lg:translate-x-0 lg:z-auto',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 py-5">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 text-white font-bold text-sm shrink-0 shadow-lg shadow-blue-500/30">
            PF
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 dark:text-white tracking-tight">PawsFree</div>
            <div className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">Admin console</div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-5">
          {ADMIN_NAV.map(section => (
            <div key={section.section}>
              <p className="px-2 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400/70 dark:text-gray-600">
                {section.section}
              </p>
              <div className="space-y-0.5">
                {section.items.map(item => {
                  const { Icon } = item;
                  const active = page === item.key;
                  return (
                    <button
                      key={item.key}
                      onClick={() => navigate(item.key)}
                      className={[
                        'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 text-left',
                        active
                          ? 'bg-blue-600 text-white shadow-md shadow-blue-500/25'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-900 dark:hover:text-gray-100',
                      ].join(' ')}
                    >
                      <Icon className="w-[15px] h-[15px] shrink-0 opacity-90" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="pt-1">
            <div className="h-px bg-gray-200 dark:bg-white/[0.06] mb-3" />
            <a
              href="/portal"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-500 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-800 dark:hover:text-gray-300 transition-all duration-150"
            >
              <HomeIcon className="w-[15px] h-[15px] shrink-0" />
              Account Portal
            </a>
          </div>
        </nav>

        {/* Footer */}
        <div className="px-3 py-3 border-t border-gray-100 dark:border-white/[0.05]">
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors group">
            <Avatar name={me.displayName} size={30} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">{me.displayName}</p>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">Owner · admin</p>
            </div>
            <button
              type="button"
              onClick={() => { void logout(); }}
              title="Sign out"
              className="p-2 rounded-md text-gray-300 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-all"
            >
              <LogoutIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Topbar */}
        <header className="flex items-center gap-3 sm:gap-4 px-4 sm:px-6 h-14 shrink-0 bg-white/80 dark:bg-[#111827]/80 backdrop-blur-sm border-b border-gray-200/80 dark:border-white/[0.06]">
          <button
            onClick={() => setNavOpen(true)}
            title="Open menu"
            aria-label="Open menu"
            className="lg:hidden -ml-1 p-2 rounded-lg text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
          >
            <ListIcon className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2 text-sm min-w-0">
            <span className="hidden sm:inline text-gray-400 dark:text-gray-500 font-medium">Admin</span>
            <ChevronRightIcon className="hidden sm:inline w-3.5 h-3.5 text-gray-300 dark:text-gray-700" />
            <span className="font-semibold text-gray-900 dark:text-gray-50 truncate">{crumb}</span>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={toggle}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
            >
              {theme === 'dark' ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <ChunkBoundary message="We couldn’t load that page. A reload usually sorts it.">
          <React.Suspense fallback={<PageSpinner />}>
          {page === 'overview'  && <Overview onNav={(p) => navigate(p as AdminPage)} />}
          {page === 'costs'     && <Costs />}
          {page === 'controls'  && <Controls />}
          {page === 'users' && !selectedUserId && <Users onSelectUser={setSelectedUserId} />}
          {page === 'users' && selectedUserId  && <UserDetail accountId={selectedUserId} onBack={() => setSelectedUserId(null)} />}
          {page === 'orgs' && !selectedOrgId && <Orgs onSelectOrg={setSelectedOrgId} />}
          {page === 'orgs' && selectedOrgId  && (
            <OrgDetail
              orgId={selectedOrgId}
              onBack={() => setSelectedOrgId(null)}
              onSelectUser={(id) => { setSelectedOrgId(null); setSelectedUserId(id); go('users'); }}
            />
          )}
          {page === 'plans'      && <Plans onSelectUser={(id) => { setSelectedUserId(id); go('users'); }} />}
          {page === 'templates'  && <AdminTemplates />}
          {page === 'emails'     && (
            <EmailTemplates initialKind={routeId} onSelectTemplate={(kind) => go('emails', kind)} />
          )}
          {page === 'requests'   && <Requests initialSelectedId={routeId} />}
          {page === 'recordings' && (
            <Recordings
              recordingId={routeId}
              onSelectRecording={(id) => go('recordings', id)}
              onOpenPatient={(id) => go('patients', id)}
            />
          )}
          {page === 'patients' && (
            <Patients
              patientId={routeId}
              onSelectPatient={(id) => go('patients', id)}
              onOpenRecording={(id) => go('recordings', id)}
              onOpenRequest={(id) => go('requests', id)}
              onOpenUser={(id) => { setSelectedUserId(id); go('users'); }}
            />
          )}
          {page === 'bugreports' && <BugReports onOpenRequest={(id) => go('requests', id)} />}
          {page === 'referrals'  && <AdminReferrals />}
          {page === 'links'      && <AdminLinks />}
          {page === 'paywalls'   && <AdminPaywalls />}
          {page === 'security'   && <Security />}
          {page === 'events'     && <Events />}
          {page === 'consents'   && <Consents onSelectUser={(id) => { setSelectedUserId(id); go('users'); }} />}
          </React.Suspense>
          </ChunkBoundary>
        </main>
      </div>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────

// The public signup funnel. Lazy so an ad click doesn't download the admin
// console: every admin page above is imported statically, which puts Tremor and
// all 14 of them in the entry chunk. Splitting the funnel off is only half the
// job — see the Suspense boundaries below, which split the console off too.
const Welcome = React.lazy(() => import('./pages/welcome/Welcome.js').then(m => ({ default: m.Welcome })));
const Install = React.lazy(() => import('./pages/welcome/Install.js').then(m => ({ default: m.Install })));

export const App: React.FC = () => {
  const path = window.location.pathname;
  const isAdminPath  = path.startsWith('/admin');
  const isPortalPath = path.startsWith('/portal');
  const isWelcomePath = path === '/welcome' || path.startsWith('/welcome/');

  if (path === '/reset-password') return <ResetPassword />;

  // Before the /api/user/me query below, because the funnel is served to people
  // who by definition have no account yet — asking who they are first would
  // render a login card over the signup page. Both routes resolve auth
  // themselves, with a same-origin fetch that carries the SameSite=strict
  // session cookie the redirect back from Google could not.
  if (isWelcomePath) {
    return (
      <ChunkBoundary message="We couldn’t finish loading the page. A reload usually sorts it.">
        <React.Suspense fallback={null}>
          {path.startsWith('/welcome/install') ? <Install /> : <Welcome />}
        </React.Suspense>
      </ChunkBoundary>
    );
  }

  if (!isAdminPath && !isPortalPath) return null;

  const { data: me, isLoading, isError } = useQuery<UserMeResponse>({
    queryKey: ['me'],
    queryFn: () => api.get<UserMeResponse>('/api/user/me'),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    );
  }

  if (isError || !me) return <Login />;

  if (isAdminPath && me.isAdmin) return <AdminApp me={me} />;

  if (isAdminPath && !me.isAdmin) {
    window.location.replace('/portal');
    return null;
  }

  return (
    <ChunkBoundary message="We couldn’t load your account. A reload usually sorts it.">
      <React.Suspense fallback={<PageSpinner />}>
        <AccountShell me={me} />
      </React.Suspense>
    </ChunkBoundary>
  );
};
